import numeral from "numeral"
import {toDollars, toCents} from "../../../../utilities/numbers"
import {
	saveAccountLimitOverride,
	saveWireLimitOverride,
	saveCheckDepositLimitOverride,
	saveCheckPaymentLimitOverride,
	revertSingleLimitOverride,
	getAccountConfigOverrides,
	isOverrideActive,
	LimitOverrideType,
} from "../../../../resources/account"

export enum EditMode {
	Edit = "edit",
	Revert = "revert",
}

export interface ExistingOverride {
	amount: number
	expiredAt?: string
	overrideId?: string
}

export interface LimitOverrideModalProps {
	close: () => void
	limitType: LimitOverrideType
	currentLimit: number
	depositProductLimit: number
	existingOverride?: ExistingOverride
	accountId: string
	onSuccess: () => void
}

export const limitTypeLabels: Record<LimitOverrideType, {category: string; name: string}> = {
	[LimitOverrideType.CardDailyPurchase]: {category: "Card", name: "Purchases (Daily Limit)"},
	[LimitOverrideType.CardDailyTransaction]: {category: "Card", name: "Transaction (Daily Limit)"},
	[LimitOverrideType.CardDailyDeposit]: {category: "Card", name: "ATM deposit (Daily Limit)"},
	[LimitOverrideType.CardDailyWithdrawal]: {category: "Card", name: "ATM Withdrawal (Daily Limit)"},
	[LimitOverrideType.AchDailyCredit]: {category: "ACH", name: "Credit (Daily Limit)"},
	[LimitOverrideType.AchDailyDebit]: {category: "ACH", name: "Debit (Daily Limit)"},
	[LimitOverrideType.AchMonthlyCredit]: {category: "ACH", name: "Credit (Monthly Limit)"},
	[LimitOverrideType.AchMonthlyDebit]: {category: "ACH", name: "Debit (Monthly Limit)"},
	[LimitOverrideType.WireDailyTransfer]: {category: "Wire", name: "Transfers (Daily Limit)"},
	[LimitOverrideType.WireMonthlyTransfer]: {category: "Wire", name: "Transfers (Monthly Limit)"},
	[LimitOverrideType.CheckDepositDaily]: {category: "Check Deposit", name: "Check Deposits (Daily Limit)"},
	[LimitOverrideType.CheckDepositMonthly]: {category: "Check Deposit", name: "Check Deposits (Monthly Limit)"},
	[LimitOverrideType.CheckPaymentDaily]: {category: "Check Payment", name: "Sent (Daily Limit)"},
	[LimitOverrideType.CheckPaymentMonthly]: {category: "Check Payment", name: "Sent (Monthly Limit)"},
}

export function formatCurrency(cents: number): string {
	return numeral(toDollars(cents)).format("$0,0.00")
}

export function parseDollarInput(value: string): number {
	const cleaned = value.replace(/[^0-9.]/g, "")
	return toCents(cleaned)
}

export function formatDisplayDate(dateStr: string): string {
	if (!dateStr) return ""
	const [year, month, day] = dateStr.split("-").map(Number)
	const date = new Date(year, month - 1, day)
	return date.toLocaleDateString("en-US", {month: "numeric", day: "numeric", year: "numeric"})
}

export function formatDateForInput(isoDate?: string): string {
	if (!isoDate) return ""
	return isoDate.split("T")[0]
}

export function isOneOf<T extends LimitOverrideType>(value: LimitOverrideType, values: readonly T[]): value is T {
	return (values as readonly LimitOverrideType[]).includes(value)
}

export const achLimitTypes = [
	LimitOverrideType.AchDailyCredit,
	LimitOverrideType.AchDailyDebit,
	LimitOverrideType.AchMonthlyCredit,
	LimitOverrideType.AchMonthlyDebit,
] as const

export const wireLimitTypes = [LimitOverrideType.WireDailyTransfer, LimitOverrideType.WireMonthlyTransfer] as const
export const checkDepositLimitTypes = [
	LimitOverrideType.CheckDepositDaily,
	LimitOverrideType.CheckDepositMonthly,
] as const
export const checkPaymentLimitTypes = [
	LimitOverrideType.CheckPaymentDaily,
	LimitOverrideType.CheckPaymentMonthly,
] as const

function getOverrideType(limitType: LimitOverrideType): string {
	if (isOneOf(limitType, achLimitTypes)) return "achLimitOverride"
	if (isOneOf(limitType, wireLimitTypes)) return "wireLimitOverride"
	if (isOneOf(limitType, checkDepositLimitTypes)) return "checkDepositLimitOverride"
	if (isOneOf(limitType, checkPaymentLimitTypes)) return "checkPaymentLimitOverride"
	return ""
}

export async function submitLimitOverride({
	accessToken,
	accountId,
	limitType,
	amount,
	endDate,
	overrideId,
	isReverting,
}: {
	accessToken: string
	accountId: string
	limitType: LimitOverrideType
	amount: number
	endDate?: string
	overrideId?: string
	isReverting: boolean
}): Promise<{ok: true} | {ok: false; error: string}> {
	if (isReverting) {
		if (!overrideId) {
			return {ok: false, error: "Cannot revert: override ID not found"}
		}

		const overridesResult = await getAccountConfigOverrides(accessToken, accountId)
		if (!overridesResult.isOk()) {
			return {ok: false, error: "Failed to fetch current overrides"}
		}

		const overrideType = getOverrideType(limitType)
		const currentOverride = overridesResult.value.find(
			(o) => o.id === overrideId && o.type === overrideType && isOverrideActive(o)
		)
		if (!currentOverride) {
			return {ok: false, error: "Override not found or already canceled"}
		}

		const result = await revertSingleLimitOverride(accessToken, overrideId, limitType, accountId, currentOverride)
		if (result.isOk()) return {ok: true}
		return {
			ok: false,
			error: result.error.errors?.[0]?.title || "An error occurred while reverting the limit override.",
		}
	}

	let result

	if (isOneOf(limitType, achLimitTypes)) {
		result = await saveAccountLimitOverride(accessToken, accountId, {limitType, amount, endDate}, overrideId)
	} else if (isOneOf(limitType, wireLimitTypes)) {
		result = await saveWireLimitOverride(accessToken, accountId, {limitType, amount, endDate}, overrideId)
	} else if (isOneOf(limitType, checkDepositLimitTypes)) {
		result = await saveCheckDepositLimitOverride(accessToken, accountId, {limitType, amount, endDate}, overrideId)
	} else if (isOneOf(limitType, checkPaymentLimitTypes)) {
		result = await saveCheckPaymentLimitOverride(accessToken, accountId, {limitType, amount, endDate}, overrideId)
	} else {
		return {ok: false, error: "This limit type is not yet supported for overrides."}
	}

	if (result.isOk()) return {ok: true}
	return {ok: false, error: result.error.errors?.[0]?.title || "An error occurred while saving the limit override."}
}
